import {
    Icon20ArrowUturnLeftOutline,
    Icon20Check,
    Icon20ChevronUpOutline,
    Icon20CloudArrowUpOutline,
    Icon20PicturePlusOutline,
    Icon20StarsFilled,
    Icon24GradientOutline,
} from '@vkontakte/icons';
import classNames from 'clsx';
import starsAnimation from 'img/aiTools/starsWhiteMoving.gif';
import { ReactComponent as Icon20Swap } from 'img/icons/swap.svg';
import React, { memo, useCallback, useMemo } from 'react';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import type { ProcessingType } from 'reactApp/modules/aiTools/aiTools.types';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { type ListItem, DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { useDropdownList } from 'reactApp/ui/DropdownList/DropdownList.hooks';

import styles from './AiImproveToolbar.css';

export const ImproveButton = memo<{ isProcessing: boolean; onClick(): void }>(({ isProcessing, onClick }) => {
    return (
        <div
            className={classNames(styles.button, {
                [styles.button_processing]: isProcessing,
            })}
            onClick={onClick}
        >
            {isProcessing ? (
                <img src={starsAnimation} className={styles.starsAnimation} data-qa-id="improve-process-icon" />
            ) : (
                <Icon20StarsFilled data-qa-id="improve-icon" />
            )}
            {isProcessing ? 'Нейросеть улучшает фото' : 'Улучшить с нейросетью'}
        </div>
    );
});

ImproveButton.displayName = 'ImproveButton';

export const RemoveBackgroundButton = memo<{ isProcessing: boolean; onClick(): void }>(({ isProcessing, onClick }) => {
    return (
        <div
            className={classNames(styles.button, {
                [styles.button_processing]: isProcessing,
            })}
            onClick={onClick}
        >
            {isProcessing ? (
                <img src={starsAnimation} className={styles.starsAnimation} data-qa-id="improve-process-icon" />
            ) : (
                <Icon24GradientOutline width={20} height={20} data-qa-id="rmv-back-icon" />
            )}
            {isProcessing ? 'Нейросеть удаляет фон' : 'Удалить фон'}
        </div>
    );
});

RemoveBackgroundButton.displayName = 'RemoveBackgroundButton';

export const ProgressButton = memo<{ type?: ProcessingType }>(({ type }) => {
    return (
        <div className={classNames(styles.button, styles.button_processing)}>
            <img src={starsAnimation} className={styles.starsAnimation} data-qa-id="improve-process-icon" />
            {type === 'improve' ? 'Нейросеть улучшает фото' : 'Нейросеть удаляет фон'}
        </div>
    );
});

ProgressButton.displayName = 'ProgressButton';

export const CancelButton = memo<{ onClick(): void; type?: ProcessingType; isNewbackground?: boolean }>(
    ({ onClick, type, isNewbackground }) => {
        let text = 'Отменить';
        if (type === 'removeBackground') {
            text = isNewbackground ? 'Вернуть старый фон' : 'Вернуть фон';
        }
        return (
            <div className={styles.button} onClick={onClick}>
                <Icon20ArrowUturnLeftOutline data-qa-id="cancel-icon" />
                {text}
            </div>
        );
    }
);

CancelButton.displayName = 'CancelButton';

export const CompareButton = memo<{ onClick(): void; active?: boolean }>(({ onClick, active = false }) => {
    return (
        <div className={classNames(styles.button, { [styles.button_active]: active })} onClick={onClick}>
            <Icon20Swap data-qa-id="compare-icon" />
            Сравнить
        </div>
    );
});

CompareButton.displayName = 'CompareButton';

const renderDropdownItem = (item) => {
    return <DropdownItemAction active={false} text={item.text} icon={item.icon} />;
};

export const SaveButton = memo<{
    onClick(saveAsNew: boolean): void;
    showSaveVariants?: boolean;
    type?: ProcessingType;
}>(({ onClick, showSaveVariants = true, type }) => {
    const { controlRef, isOpen, open, close, findDropdownPosition } = useDropdownList<HTMLDivElement>({
        offsetTop: -45,
        offsetLeft: 115,
        onTop: true,
    });

    const menuItems: ListItem[] = useMemo(
        () => [
            {
                id: 'saveAs',
                text: type === 'removeBackground' ? 'И без фона, и оригинал' : 'И улучшенное, и оригинал',
                onClick: () => {
                    onClick(true);
                },
                icon: <Icon20PicturePlusOutline />,
            },
            {
                id: 'save',
                text: type === 'removeBackground' ? 'Без фона' : 'Только улучшенное фото',
                onClick: () => onClick(false),
                icon: <Icon20Check />,
            },
        ],
        [onClick, type]
    );

    const handleVariantsClick = useCallback(() => {
        if (isOpen) {
            close();
        } else {
            open();
        }
    }, [isOpen, close, open]);

    return (
        <>
            <div
                className={styles.button}
                onClick={showSaveVariants ? handleVariantsClick : () => onClick(true)}
                ref={controlRef}
                data-qa-id="bttn-save"
            >
                <Icon20CloudArrowUpOutline data-qa-id="save-icon" />
                {showSaveVariants ? 'Сохранить' : 'Сохранить в Облако'}
                {showSaveVariants && <Icon20ChevronUpOutline className={classNames(styles.upIcon, { [styles.downIcon]: isOpen })} />}
            </div>
            {isOpen && showSaveVariants && (
                <DropdownList
                    list={menuItems}
                    className={classNames(styles.dropdown, VK_UI_DARK_THEME)}
                    renderItem={renderDropdownItem}
                    theme={DropdownTheme.space}
                    parentRef={controlRef}
                    closeOnResize
                    gaId="save"
                    close={close}
                    calcPosition={findDropdownPosition}
                />
            )}
        </>
    );
});

SaveButton.displayName = 'SaveButton';

export const Delimiter = memo(() => {
    return <div className={styles.delimiter} />;
});

Delimiter.displayName = 'Delimiter';
